/**
 * videoCallApi — cliente REST da chamada de vídeo do atendente (/api/inbox/video-calls).
 *
 * Todo erro vira `VideoCallApiError` com o `code` do backend (`FEATURE_BLOCKED`,
 * `DELIVERY_OPTION_UNAVAILABLE` + `reason`, `INVALID_LINK_TTL`, `VIDEO_CALL_DISABLED`,
 * `MEDIA_SERVER_UNAVAILABLE`, `VIDEO_CAPACITY_EXHAUSTED`, `DELIVERY_FAILED`...): a tela decide
 * pelo `code`, nunca pelo texto. O `apiFetch` lança nos 5xx (com o corpo em `err.data`) e
 * devolve a resposta nos 4xx — os dois caminhos chegam aqui com o mesmo formato.
 */
import { authFetch } from '@/utils/api';

const BASE = '/api/inbox/video-calls';

export class VideoCallApiError extends Error {
    /**
     * @param {string} message
     * @param {{ status?: number, code?: string, reason?: string|null, data?: object }} [info]
     */
    constructor(message, { status = 0, code = 'UNKNOWN', reason = null, data = null } = {}) {
        super(message);
        this.name = 'VideoCallApiError';
        this.status = status;
        this.code = code;
        this.reason = reason;
        this.data = data;
    }
}

function toError(status, data, fallback) {
    return new VideoCallApiError(data?.error || data?.message || fallback, {
        status,
        code: data?.code || 'UNKNOWN',
        reason: data?.reason ?? null,
        data,
    });
}

async function request(path, options = {}) {
    let res;
    try {
        res = await authFetch(`${BASE}${path}`, options);
    } catch (err) {
        if (err?.data?.code) throw toError(err.status, err.data, err.message);
        throw new VideoCallApiError(err?.message || 'Erro na requisição', { status: err?.status ?? 0 });
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw toError(res.status, data, 'Erro na requisição');
    return data;
}

const post = (path, body) => request(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

/** Formas de envio disponíveis para o contato (VCALL-07..13). */
export function getVideoCallOptions({ contactId = null, ticketId = null } = {}) {
    const qs = new URLSearchParams();
    if (contactId) qs.set('contactId', contactId);
    if (ticketId) qs.set('ticketId', ticketId);
    const query = qs.toString();
    // Sem contato são as opções de uma sala sem contato: só o link, os limites de validade e o bloqueio.
    return request(query ? `/options?${query}` : '/options');
}

/**
 * Cria a chamada. Sem `contactId` é uma sala sem contato ("Iniciar agora" / "Criar link para depois"): só `LINK`, sem ticket.
 * @param {{ contactId?: string|null, ticketId?: string|null, deliveryMethod: 'APP'|'CHANNEL'|'LINK',
 *   channelId?: string|null, entryMode: 'OPEN'|'APPROVAL', title?: string|null, ttlMinutes?: number|null,
 *   transcribe?: boolean }} body `transcribe: true` liga a transcrição (Jev) desta chamada; exige o plano com
 *   `calling.video_transcription` (`options.transcription.available`), senão 403 `TRANSCRIPTION_NOT_AVAILABLE`.
 */
export function startVideoCall(body) {
    return post('', body);
}

/** Detalhe de uma chamada: participantes, consentimento e, para o criador de uma sala `LINK` aberta, o `joinUrl`. */
export function getVideoCallDetail(videoCallId) {
    return request(`/${encodeURIComponent(videoCallId)}`);
}

/** Token novo para o criador voltar à chamada (VCALL-17, 37); numa sala `LINK` vem também o `joinUrl`. */
export function rejoinVideoCall(videoCallId) {
    return post(`/${encodeURIComponent(videoCallId)}/join`);
}

/** Encerra a chamada para todos (VCALL-20). */
export function endVideoCall(videoCallId) {
    return post(`/${encodeURIComponent(videoCallId)}/end`);
}

/** "Sair" depois de 10 min sem ninguém entrar (VCALL-36). */
export function leaveNoOneJoined(videoCallId) {
    return post(`/${encodeURIComponent(videoCallId)}/no-one-joined/leave`);
}

/** Aprova o convidado da sala de espera (VCALL-29). */
export function admitGuest({ videoCallId, participantId }) {
    return post(`/${encodeURIComponent(videoCallId)}/participants/${encodeURIComponent(participantId)}/admit`);
}

/** Recusa o convidado da sala de espera (VCALL-29). */
export function rejectGuest({ videoCallId, participantId }) {
    return post(`/${encodeURIComponent(videoCallId)}/participants/${encodeURIComponent(participantId)}/reject`);
}

/**
 * Pesquisa de qualidade ao fim da chamada (opcional, uma resposta por chamada).
 * @param {{ videoCallId: string, audioRating?: number|null, videoRating?: number|null,
 *   connectionQuality?: { local?: object, remote?: object }|null }} params
 */
export function submitQualitySurvey({ videoCallId, audioRating = null, videoRating = null, connectionQuality = null }) {
    return post(`/${encodeURIComponent(videoCallId)}/quality-survey`, { audioRating, videoRating, connectionQuality });
}

/**
 * Histórico keyset (VCALL-40).
 * @param {{ filters?: { from?: string, to?: string, agentId?: string, contactId?: string,
 *   status?: string[], deliveryMethod?: string, q?: string }, cursor?: string|null, limit?: number }} params
 *   `q`: busca pelo título ou pelo nome do contato (2 letras ou mais).
 */
export function getVideoCallHistory({ filters = {}, cursor = null, limit } = {}) {
    const qs = new URLSearchParams();
    for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') continue;
        if (Array.isArray(value)) {
            if (value.length) qs.set(key, value.join(','));
        } else {
            qs.set(key, String(value));
        }
    }
    if (cursor) qs.set('cursor', cursor);
    if (limit) qs.set('limit', String(limit));
    const query = qs.toString();
    return request(query ? `?${query}` : '');
}

/** Chamada não terminal do atendente, ou `null` (VCALL-37). */
export async function getActiveVideoCall() {
    const { call } = await request('/active');
    return call ?? null;
}

/**
 * Texto da chamada (falas por pessoa e momentos) para a lista inicial e a volta à chamada. O texto novo chega
 * pela sala (canal de dados); isto cobre o que veio antes de o atendente entrar ou enquanto ele esteve fora.
 * @returns {Promise<{ enabled: boolean, startedAt: string|null, segments: object[], moments: Array<{ id: string, at: number }> }>}
 */
export function getVideoCallTranscript(videoCallId) {
    return request(`/${encodeURIComponent(videoCallId)}/transcript`);
}

/**
 * Por que a Jev não está na sala (processo fora do ar, desligado, só na cor em espera, falha ao entrar). Separado da lista
 * do texto: funciona mesmo quando o armazenamento do texto falha.
 * @returns {Promise<{ enabled: boolean, state: string|null, code?: string }>}
 */
export function getVideoCallTranscriberStatus(videoCallId) {
    return request(`/${encodeURIComponent(videoCallId)}/transcriber`);
}

/** Marca um momento agora (a hora é a do servidor). @returns {Promise<{ id: string, at: number }>} */
export function addVideoCallMoment(videoCallId) {
    return post(`/${encodeURIComponent(videoCallId)}/moments`);
}

/** Desfaz um momento marcado. Idempotente. @returns {Promise<{ removed: boolean }>} */
export function removeVideoCallMoment({ videoCallId, momentId }) {
    return request(`/${encodeURIComponent(videoCallId)}/moments/${encodeURIComponent(momentId)}`, { method: 'DELETE' });
}

// ── Gravação da chamada (docs/specs/otto/RECORDING.md) ─────────────

const enc = encodeURIComponent;

/**
 * Tudo que a tela da gravação precisa: o estado (ocioso, esperando o aceite, gravando), se quem vê é quem grava, a cota do mês
 * (só de quem criou a chamada), se pode baixar e as gravações com a validade.
 * @returns {Promise<{ enabled: boolean, state: 'idle'|'requested'|'recording', canControl: boolean, canDownload: boolean,
 *   allowance: object|null, recordings: object[] }>}
 */
export function getVideoCallRecording(videoCallId) {
    return request(`/${enc(videoCallId)}/recording`);
}

/** Pede para gravar. `{ state: 'recording' }` começa na hora; `{ state: 'requested' }` espera o aceite dos convidados. */
export function requestVideoCallRecording(videoCallId) {
    return post(`/${enc(videoCallId)}/recording/request`, {});
}

/** Para a gravação, ou cancela o pedido que ainda espera as respostas. */
export function stopVideoCallRecording(videoCallId) {
    return post(`/${enc(videoCallId)}/recording/stop`, {});
}

/** URL assinada de 15 minutos para ver (ou baixar) a gravação. @returns {Promise<{ url: string, expiresAt: string, download: boolean }>} */
export function getVideoCallRecordingUrl({ videoCallId, recordingId, download = false }) {
    return request(`/${enc(videoCallId)}/recordings/${enc(recordingId)}/url${download ? '?download=1' : ''}`);
}

/** Apaga a gravação (o arquivo sai do armazenamento). */
export function deleteVideoCallRecording({ videoCallId, recordingId }) {
    return request(`/${enc(videoCallId)}/recordings/${enc(recordingId)}`, { method: 'DELETE' });
}

